import TextArea from "../../../../Components/Common/TextArea/TextArea";
import Input from "../../../../Components/Common/Input/Input";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import { useMemo, useState } from "react";
import {
  Metrics,
  Project,
  ResidentialLineItem,
  ResidentialPhase,
  StatusOption,
} from "../types/inspector.types";
import Button from "Components/Common/Button/Button";

interface ResidentialFiTabProps {
  project: Project;
  phases: ResidentialPhase[];
  gcBilledPct: string;
  metrics: Metrics;
  collapsed: Record<string, boolean>;
  statusOptions: StatusOption[];
  onTogglePhase: (phaseId: string) => void;
  onSetStatus: (itemId: string, status: number) => void;
  onSetCustomPct: (itemId: string, value: string) => void;
  onClearCustomPct: (itemId: string) => void;
  onToggleFlag: (itemId: string) => void;
  onFlagWithComment: (itemId: string, text: string) => void;
  onAddComment: (itemId: string, text: string) => void;
}

function getEstimateInfo(item: ResidentialLineItem): {
  text: string;
  cls: string;
} {
  const val = item.customPct !== "" ? Number(item.customPct) : item.status;
  if (val === undefined) return { text: "—", cls: "gcValOk" };
  if (val === 0) return { text: "0%", cls: "gcValOver" };
  if (val >= 80) return { text: `${val}%`, cls: "gcValOk" };
  return { text: `${val}%`, cls: "gcValWarn" };
}

export default function ResidentialFiTab({
  project,
  phases,
  gcBilledPct,
  metrics,
  collapsed,
  statusOptions,
  onTogglePhase,
  onSetStatus,
  onSetCustomPct,
  onClearCustomPct,
  onToggleFlag,
  onFlagWithComment,
  onAddComment,
}: ResidentialFiTabProps) {
  const [pendingFlags, setPendingFlags] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const phasePcts = useMemo(() => {
    const result: Record<string, number> = {};
    phases.forEach((phase) => {
      const pct = phase.items.reduce((sum, item) => {
        const effectivePct =
          item.customPct !== "" ? Number(item.customPct) : (item.status ?? 0);
        return sum + (effectivePct / 100) * item.weight;
      }, 0);
      result[phase.id] = Math.round(pct);
    });
    return result;
  }, [phases]);

  const handleFlagClick = (item: ResidentialLineItem) => {
    if (item.flagged) {
      onToggleFlag(item.id);
      return;
    }
    setPendingFlags((prev) => ({ ...prev, [item.id]: true }));
    requestAnimationFrame(() => {
      document.getElementById(`res-notes-${item.id}`)?.focus();
    });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    item: ResidentialLineItem,
  ) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const text = (drafts[item.id] ?? "").trim();
    if (!text) return;
    if (pendingFlags[item.id]) {
      onFlagWithComment(item.id, text);
      setPendingFlags((prev) => ({ ...prev, [item.id]: false }));
    } else {
      onAddComment(item.id, text);
    }
    setDrafts((prev) => ({ ...prev, [item.id]: "" }));
  };

  const handleBlur = (item: ResidentialLineItem) => {
    if (pendingFlags[item.id] && !(drafts[item.id] ?? "").trim()) {
      setPendingFlags((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  return (
    <>
      <div className="drawBanner">
        <div className="drawBannerLeft">
          <span>Current Draw</span>
          {project.draw} · {project.bank}
        </div>
        <div className="drawBannerRight">{metrics.avgPct}%</div>
      </div>

      <div className="totalsStrip">
        <div className="totalsTitle">Overall Progress</div>
        <div className="totalsGrid">
          <div className="totalItem">
            <div className="totalLabel">CSC Estimate</div>
            <div className="totalVal blue">{metrics.avgPct}%</div>
            <div className="totalSub">All phases</div>
          </div>
          <div className="totalItem">
            <div className="totalLabel">GC Billed</div>
            <div className="totalVal amber">{gcBilledPct}</div>
            <div className="totalSub">{project.draw}</div>
          </div>
          <div className="totalItem">
            <div className="totalLabel">Flags Raised</div>
            <div className="totalVal red">{metrics.flagsCount}</div>
            <div className="totalSub">Line items</div>
          </div>
          <div className="totalItem">
            <div className="totalLabel">Photos</div>
            <div className="totalVal green">{metrics.photosCount}</div>
            <div className="totalSub">Captured</div>
          </div>
        </div>
      </div>

      {phases.map((phase) => {
        const isCollapsed = Boolean(collapsed[phase.id]);
        return (
          <div className="phaseSection" key={phase.id}>
            <div
              className={`phaseHeader${isCollapsed ? " collapsed" : ""}`}
              onClick={() => onTogglePhase(phase.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onTogglePhase(phase.id);
              }}
            >
              <div className="phLeft">
                <div className="phLabel">{phase.label}</div>
                <div className="phWeight">{phase.weightPct}% of total</div>
              </div>
              <div className="phRight">
                <div className="phPct">{phasePcts[phase.id] ?? 0}%</div>
                <div className="phChevron">▼</div>
              </div>
            </div>

            <div className={`phaseBody${isCollapsed ? " collapsed" : ""}`}>
              {phase.items.map((item) => {
                const activeStatus =
                  item.customPct !== "" ? Number(item.customPct) : item.status;
                const { text: estText, cls: estCls } = getEstimateInfo(item);
                const isPending = Boolean(pendingFlags[item.id]);
                const draft = drafts[item.id] ?? "";
                const flagComment = item.flagComment ?? "";
                const extraComments = item.extraComments ?? [];
                const hasComments = flagComment || extraComments.length > 0;

                return (
                  <div
                    className={`fiItem${item.flagged ? " flagged" : ""}`}
                    key={item.id}
                  >
                    <div className="fiItemTop">
                      <div className={`fiNum${item.flagged ? " flagged" : ""}`}>
                        {item.num}
                      </div>
                      <div
                        className={`fiDesc${item.flagged ? " flagged" : ""}`}
                      >
                        {item.desc}
                      </div>
                      <div className="fiItemWeight">{item.weight}%</div>
                    </div>

                    <div className="gcCompare">
                      <div className="gcEstimate">
                        <div className="gcLabel">Your Estimate</div>
                        <div className={`gcVal ${estCls}`}>{estText}</div>
                      </div>
                      <Button
                        className={`flagToggle${item.flagged ? " flagged" : ""}${isPending ? " pending" : ""}`}
                        onClick={() => handleFlagClick(item)}
                        aria-pressed={item.flagged}
                      >
                        {item.flagged ? "⚠ Flagged" : "Flag"}
                      </Button>
                    </div>

                    <div className="statusBtns">
                      {statusOptions.map((option) => {
                        const selClass =
                          activeStatus === option.value
                            ? ` sel${option.value}`
                            : "";
                        return (
                          <div
                            key={`${item.id}-${option.value}`}
                            className={`statusBtn${selClass}`}
                            onClick={() => onSetStatus(item.id, option.value)}
                            role="button"
                            tabIndex={0}
                            aria-pressed={activeStatus === option.value}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                onSetStatus(item.id, option.value);
                            }}
                          >
                            {option.title}
                            <br />
                            <span className="statusSubtitle">
                              {option.subtitle}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="customPctWrap">
                      <span className="customPctLabel">Override %:</span>
                      <Input
                        className={`customPctInput${item.customPct !== "" ? " overridden" : ""}`}
                        type="number"
                        min="0"
                        max="100"
                        placeholder="—"
                        value={item.customPct}
                        onChange={(e) =>
                          onSetCustomPct(item.id, e.target.value)
                        }
                        aria-label={`Custom percentage for ${item.desc}`}
                      />
                      <Button
                        className="customPctClear"
                        onClick={() => onClearCustomPct(item.id)}
                        aria-label="Clear custom percentage"
                      >
                        ✕
                      </Button>
                    </div>

                    {hasComments && (
                      <div className="committedComments">
                        {flagComment && (
                          <div className="committedComment flag">
                            ⚠ {flagComment}
                          </div>
                        )}
                        {extraComments.map((c, i) => (
                          <div className="committedComment extra" key={i}>
                            {c}
                          </div>
                        ))}
                      </div>
                    )}

                    <TextArea
                      id={`res-notes-${item.id}`}
                      className="fiNotes"
                      placeholder={`Note for ${item.desc}`}
                      value={draft}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [item.id]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => handleKeyDown(e, item)}
                      onBlur={() => handleBlur(item)}
                      aria-label={`Notes for ${item.desc}`}
                      maxChars={CHAR_LIMIT.DESCRIPTION}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}
